import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '../../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import '../Support.css';

// Import images
import destekHeroImage from '../../assets/images/destek.jpg';

const SupportEN = () => {
  const faqs = [
    {
      id: 1,
      question: 'What is ProgenX Academy?',
      answer: 'ProgenX Academy is an innovative education platform that offers AI-powered personalized learning experiences and creates optimized educational programs according to students\' individual learning needs.'
    },
    {
      id: 2,
      question: 'Which devices can I access from?',
      answer: 'You can access ProgenX Academy from computers, tablets, and smartphones. You can use it through a web browser or via mobile application.'
    },
    {
      id: 3,
      question: 'What are the payment options?',
      answer: 'You can pay with credit card, debit card, or wire transfer/EFT. Different payment options are also available for corporate agreements.'
    },
    {
      id: 4,
      question: 'What age groups are the contents suitable for?',
      answer: 'Our contents are prepared for elementary, middle, and high school students. There are customized contents suitable for every level.'
    },
    {
      id: 5,
      question: 'I forgot my password, what should I do?',
      answer: 'You can click on the "Forgot Password" link on the login page to have a reset link sent to your email address.'
    }
  ];

  const SupportCard = ({ icon, title, description, buttonText, buttonLink, isExternal }) => {
    const ButtonComponent = isExternal ? 'a' : Link;
    const buttonProps = isExternal 
      ? { href: buttonLink, target: '_blank', rel: 'noopener noreferrer' }
      : { to: buttonLink };
    
    return (
      <div className="support-card">
        <FontAwesomeIcon icon={icon} className="support-card-icon" />
        <h3 className="support-card-title">{title}</h3>
        <p className="support-card-description">{description}</p>
        <ButtonComponent {...buttonProps} className="btn btn-primary">
          {buttonText}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <div className="support">
      <Helmet>
        <title>ProgenX Support | Help Center and Frequently Asked Questions 2025</title>
        <meta name="description" content="ProgenX support page. Frequently asked questions, email and phone support. Get help on ProgenX Academy platform usage, subscriptions, technical issues, and other topics. 24/7 customer support." />
        <meta name="keywords" content="progenx support, progenx help, progenx faq, progenx troubleshooting, progenx customer service, progenx contact, progenx technical support, progenx user guide" />
        <meta property="og:title" content="ProgenX Support | Help Center" />
        <meta property="og:description" content="Comprehensive support center for ProgenX users. Frequently asked questions and contact options." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/en/support" />
        <link rel="canonical" href="https://progenxacademy.com/en/support" />
        <link rel="alternate" hreflang="tr" href="https://progenxacademy.com/destek" />
        <link rel="alternate" hreflang="en" href="https://progenxacademy.com/en/support" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is ProgenX Academy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX Academy is an innovative education platform that offers AI-powered personalized learning experiences and creates optimized educational programs according to students' individual learning needs."
                }
              },
              {
                "@type": "Question",
                "name": "Which devices can I access from?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can access ProgenX Academy from computers, tablets, and smartphones. You can use it through a web browser or via mobile application."
                }
              },
              {
                "@type": "Question",
                "name": "What are the payment options?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can pay with credit card, debit card, or wire transfer/EFT. Different payment options are also available for corporate agreements."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Hero 
        title="SUPPORT" 
        backgroundImage={destekHeroImage}
      />
      
      <section className="support-content">
        <div className="container">
          <h2 className="section-title">How Can We Help You?</h2>
          <p className="support-intro">
            We are here to provide the best support service to ProgenX Academy users. You can choose the option that suits your needs from the options below.
          </p>
          
          <div className="support-cards">
            <SupportCard 
              icon={faQuestionCircle}
              title="Frequently Asked Questions"
              description="Find solutions quickly with frequently asked questions and answers"
              buttonText="View FAQs"
              buttonLink="#faq-section"
            />
            
            <SupportCard 
              icon={faEnvelope}
              title="Email Support"
              description="Send us an email for your questions, we will respond as soon as possible"
              buttonText="Send Email"
              buttonLink="/en/contact"
            />
            
            <SupportCard 
              icon={faPhone}
              title="Phone Support"
              description="You can reach us on weekdays between 09:00-18:00"
              buttonText="Call Us"
              buttonLink="tel:+905386864516"
              isExternal={true}
            />
          </div>
          
          <section id="faq-section" className="faq-section">
            <h3 className="faq-section-title">Frequently Asked Questions</h3>
            
            <div className="faq-list">
              {faqs.map(faq => (
                <div key={faq.id} className="faq-item">
                  <h4 className="faq-question">
                    <FontAwesomeIcon icon={faQuestionCircle} className="faq-question-icon" />
                    {faq.question}
                  </h4>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
          
          <div className="support-cta">
            <h3 className="support-cta-title">Couldn't Find Your Question?</h3>
            <p className="support-cta-text">You can contact us for more comprehensive support.</p>
            <Link to="/en/contact" className="btn btn-primary">CONTACT US</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportEN;
